import React, { useEffect, useState, forwardRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import MaterialTable from '../components/MaterialTableAdapter'
import Loading from '../components/Loading'

// Icons
import AddBox from '@mui/icons-material/AddBox'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import Check from '@mui/icons-material/Check'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Clear from '@mui/icons-material/Clear'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import Edit from '@mui/icons-material/Edit'
import FilterList from '@mui/icons-material/FilterList'
import FirstPage from '@mui/icons-material/FirstPage'
import LastPage from '@mui/icons-material/LastPage'
import Remove from '@mui/icons-material/Remove'
import SaveAlt from '@mui/icons-material/SaveAlt'
import Search from '@mui/icons-material/Search'
import ViewColumn from '@mui/icons-material/ViewColumn'
import Visibility from '@mui/icons-material/Visibility'
import Payment from '@mui/icons-material/Payment'
import PersonAdd from '@mui/icons-material/PersonAdd'
import Settings from '@mui/icons-material/Settings'
import Calculate from '@mui/icons-material/Calculate'
import Pending from '@mui/icons-material/Pending'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Cancel from '@mui/icons-material/Cancel'
import Schedule from '@mui/icons-material/Schedule'
import { useDialog } from '../components/DialogProvider';
import ScreenHeader from '../components/layout/ScreenHeader'
import { hasPermission } from '../constants/rolePermissions'

const tableIcons = {
    Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
    Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
    Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
    DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
    Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
    Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
    FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
    LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
    NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
    PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
    ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
    Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
    SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
    ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
    ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
}

const AffiliateManagement = () => {
    const { confirm, prompt } = useDialog();
    const dispatch = useDispatch()
    const navigate = useNavigate()

    // State
    const [activeTab, setActiveTab] = useState('affiliates') // 'affiliates', 'commissions', 'pending', 'calculate', 'tiers'
    const [loading, setLoading] = useState(false)
    const [affiliatesData, setAffiliatesData] = useState([])
    const [commissionsData, setCommissionsData] = useState([])
    const [pendingCommissions, setPendingCommissions] = useState([])
    const [pendingSummary, setPendingSummary] = useState({})
    const [commissionTiers, setCommissionTiers] = useState([])

    // Add Commission Modal
    const [addCommissionModal, setAddCommissionModal] = useState(false)
    const [selectedAffiliate, setSelectedAffiliate] = useState(null)
    const [commissionAmount, setCommissionAmount] = useState('')
    const [commissionDate, setCommissionDate] = useState(new Date().toISOString().split('T')[0])
    const [commissionRemarks, setCommissionRemarks] = useState('')
    const [commissionType, setCommissionType] = useState('referral') // 'referral', 'bonus', 'adjustment'

    // Process Payment Modal
    const [paymentModal, setPaymentModal] = useState(false)
    const [selectedCommission, setSelectedCommission] = useState(null)
    const [paymentMethod, setPaymentMethod] = useState('bank_transfer')
    const [paymentReference, setPaymentReference] = useState('')
    const [paymentRemarks, setPaymentRemarks] = useState('')

    // Tier Modal
    const [tierModal, setTierModal] = useState(false)
    const [editingTier, setEditingTier] = useState(null)
    const [tierForm, setTierForm] = useState({
        name: '',
        minHours: 0,
        maxHours: '',
        ratePerHour: 0,
        description: ''
    })

    // Calculate Commission State
    const [calcStartDate, setCalcStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0])
    const [calcEndDate, setCalcEndDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0])
    const [commissionPreview, setCommissionPreview] = useState(null)
    const [calculatingCommission, setCalculatingCommission] = useState(false)

    // Selected commissions for bulk actions
    const [selectedCommissionIds, setSelectedCommissionIds] = useState([])

    // Assign Affiliate Modal
    const [assignModal, setAssignModal] = useState(false)
    const [allUsers, setAllUsers] = useState([])
    const [assignPlayerId, setAssignPlayerId] = useState('')
    const [assignAffiliateId, setAssignAffiliateId] = useState('')
    const [assignSplit, setAssignSplit] = useState(100)
    const [assignBonus, setAssignBonus] = useState(0)
    const [assignNotes, setAssignNotes] = useState('')
    const [playerSearch, setPlayerSearch] = useState('')
    const [affiliateSearch, setAffiliateSearch] = useState('')

    // Session Hours Log State
    const [sessionHoursModal, setSessionHoursModal] = useState(false)
    const [sessionPlayerId, setSessionPlayerId] = useState('')
    const [sessionClubId, setSessionClubId] = useState('')
    const [sessionDate, setSessionDate] = useState(new Date().toISOString().split('T')[0])
    const [sessionHours, setSessionHours] = useState('')
    const [sessionRemarks, setSessionRemarks] = useState('')
    const [clubsList, setClubsList] = useState([])
    const [sessionPlayerSearch, setSessionPlayerSearch] = useState('')

    // Redux State
    const userLogin = useSelector((state) => state.userLogin)
    const { userInfo } = userLogin

    // Check Permissions
    useEffect(() => {
        if (!userInfo) {
            navigate('/')
        } else if (!hasPermission(userInfo.role, 'affiliateManage')) {
            toast.error('You do not have permission to view this page')
            navigate('/dashboard')
        }
    }, [userInfo, navigate])

    // Fetch Data
    useEffect(() => {
        fetchAffiliates()
        fetchCommissions()
        fetchPendingCommissions()
        fetchCommissionTiers()
        fetchAllUsers()
        fetchClubs()
    }, [])

    const fetchAffiliates = async () => {
        try {
            setLoading(true)
            const response = await fetch(`${process.env.REACT_APP_API_URL}/users/admin/affiliates`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data = await response.json()
            setAffiliatesData(data.affiliates || [])
            setLoading(false)
        } catch (error) {
            console.error('Error fetching affiliates:', error)
            toast.error('Failed to fetch affiliates: ' + error.message)
            setLoading(false)
        }
    }

    const fetchCommissions = async () => {
        try {
            setLoading(true)
            const response = await fetch(`${process.env.REACT_APP_API_URL}/users/admin/affiliate-commissions`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data = await response.json()
            setCommissionsData(data.commissions || [])
            setLoading(false)
        } catch (error) {
            console.error('Error fetching commissions:', error)
            toast.error('Failed to fetch commissions: ' + error.message)
            setLoading(false)
        }
    }

    const handleAddCommission = async () => {
        if (!selectedAffiliate || !commissionAmount) {
            toast.error('Please fill all required fields')
            return
        }

        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            }
            const response = await fetch(`${process.env.REACT_APP_API_URL}/users/admin/affiliate-commissions`, {
                method: 'POST',
                headers: config.headers,
                body: JSON.stringify({
                    affiliateId: selectedAffiliate._id,
                    amount: parseFloat(commissionAmount),
                    commissionDate: commissionDate,
                    remarks: commissionRemarks,
                    type: commissionType
                })
            })

            const data = await response.json()

            if (response.ok) {
                toast.success('Commission added successfully')
                setAddCommissionModal(false)
                resetCommissionForm()
                fetchCommissions()
                fetchAffiliates() // Refresh to update totals
            } else {
                toast.error(data.message || 'Failed to add commission')
            }
        } catch (error) {
            console.error('Error adding commission:', error)
            toast.error('Failed to add commission')
        }
    }

    const handleProcessPayment = async () => {
        if (!selectedCommission || !paymentReference) {
            toast.error('Please fill all required fields')
            return
        }

        try {
            const config = {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            }
            const response = await fetch(`${process.env.REACT_APP_API_URL}/users/admin/affiliate-commissions/${selectedCommission._id}/pay`, {
                method: 'PUT',
                headers: config.headers,
                body: JSON.stringify({
                    paymentMethod: paymentMethod,
                    paymentReference: paymentReference,
                    remarks: paymentRemarks
                })
            })

            const data = await response.json()

            if (response.ok) {
                toast.success('Payment processed successfully')
                setPaymentModal(false)
                resetPaymentForm()
                fetchCommissions()
                fetchAffiliates() // Refresh to update totals
            } else {
                toast.error(data.message || 'Failed to process payment')
            }
        } catch (error) {
            console.error('Error processing payment:', error)
            toast.error('Failed to process payment')
        }
    }

    const resetCommissionForm = () => {
        setSelectedAffiliate(null)
        setCommissionAmount('')
        setCommissionDate(new Date().toISOString().split('T')[0])
        setCommissionRemarks('')
        setCommissionType('referral')
    }

    const resetPaymentForm = () => {
        setSelectedCommission(null)
        setPaymentMethod('bank_transfer')
        setPaymentReference('')
        setPaymentRemarks('')
    }

    // ========================================================================
    // Fetch All Users (for assign affiliate dropdown)
    // ========================================================================
    const fetchAllUsers = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/users/affiliates/list`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data = await response.json()
            setAllUsers(data.affiliates || [])
        } catch (error) {
            console.error('Error fetching users:', error)
        }
    }

    // ========================================================================
    // Fetch Clubs (for session hours logging)
    // ========================================================================
    const fetchClubs = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/clubs`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data = await response.json()
            const clubList = data.clubs || data.data || data
            setClubsList(Array.isArray(clubList) ? clubList.filter(c => c.isActive) : [])
        } catch (error) {
            console.error('Error fetching clubs:', error)
        }
    }

    // ========================================================================
    // Assign Affiliate to Player
    // ========================================================================
    const handleAssignAffiliate = async () => {
        if (!assignPlayerId || !assignAffiliateId) {
            toast.error('Please select both a player and an affiliate')
            return
        }

        if (assignPlayerId === assignAffiliateId) {
            toast.error('A player cannot be their own affiliate')
            return
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/player/${assignPlayerId}/assign`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
                body: JSON.stringify({
                    affiliateId: assignAffiliateId,
                    commissionSplit: assignSplit,
                    oneTimeBonus: assignBonus,
                    notes: assignNotes
                })
            })

            const data = await response.json()

            if (response.ok) {
                toast.success('Affiliate assigned successfully')
                setAssignModal(false)
                resetAssignForm()
                fetchAffiliates()
            } else {
                toast.error(data.message || 'Failed to assign affiliate')
            }
        } catch (error) {
            console.error('Error assigning affiliate:', error)
            toast.error('Failed to assign affiliate')
        }
    }

    const resetAssignForm = () => {
        setAssignPlayerId('')
        setAssignAffiliateId('')
        setAssignSplit(100)
        setAssignBonus(0)
        setAssignNotes('')
        setPlayerSearch('')
        setAffiliateSearch('')
    }

    // ========================================================================
    // Log Session Hours
    // ========================================================================
    const handleLogSessionHours = async () => {
        if (!sessionPlayerId || !sessionClubId || !sessionHours) {
            toast.error('Please fill all required fields')
            return
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/session-hours`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
                body: JSON.stringify({
                    playerId: sessionPlayerId,
                    clubId: sessionClubId,
                    sessionDate: sessionDate,
                    hoursPlayed: parseFloat(sessionHours),
                    remarks: sessionRemarks
                })
            })

            const data = await response.json()

            if (response.ok) {
                toast.success('Session hours logged successfully')
                setSessionHoursModal(false)
                resetSessionForm()
            } else {
                toast.error(data.message || 'Failed to log session hours')
            }
        } catch (error) {
            console.error('Error logging session hours:', error)
            toast.error('Failed to log session hours')
        }
    }

    const resetSessionForm = () => {
        setSessionPlayerId('')
        setSessionClubId('')
        setSessionDate(new Date().toISOString().split('T')[0])
        setSessionHours('')
        setSessionRemarks('')
        setSessionPlayerSearch('')
    }

    // ========================================================================
    // Fetch Pending Commissions
    // ========================================================================
    const fetchPendingCommissions = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/commission/pending`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data = await response.json()
            setPendingCommissions(data.commissions || [])
            setPendingSummary(data.summary || {})
        } catch (error) {
            console.error('Error fetching pending commissions:', error)
        }
    }

    // ========================================================================
    // NEW: Fetch Commission Tiers
    // ========================================================================
    const fetchCommissionTiers = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/tiers`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const data = await response.json()
            setCommissionTiers(data.tiers || [])
        } catch (error) {
            console.error('Error fetching commission tiers:', error)
        }
    }

    // ========================================================================
    // NEW: Approve Commission
    // ========================================================================
    const handleApproveCommission = async (commissionId) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/commission/${commissionId}/approve`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
                body: JSON.stringify({ remarks: 'Approved by admin' })
            })

            const data = await response.json()

            if (response.ok) {
                toast.success('Commission approved successfully')
                fetchPendingCommissions()
                fetchCommissions()
            } else {
                toast.error(data.message || 'Failed to approve commission')
            }
        } catch (error) {
            console.error('Error approving commission:', error)
            toast.error('Failed to approve commission')
        }
    }

    // ========================================================================
    // NEW: Reject Commission
    // ========================================================================
    const handleRejectCommission = async (commissionId) => {
        const reason = await prompt({ title: 'Reject commission', message: 'Enter rejection reason:', label: 'Reason', multiline: true, confirmText: 'Reject', danger: true })
        if (!reason) {
            toast.error('Rejection reason is required')
            return
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/commission/${commissionId}/reject`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
                body: JSON.stringify({ reason })
            })

            const data = await response.json()

            if (response.ok) {
                toast.success('Commission rejected')
                fetchPendingCommissions()
                fetchCommissions()
            } else {
                toast.error(data.message || 'Failed to reject commission')
            }
        } catch (error) {
            console.error('Error rejecting commission:', error)
            toast.error('Failed to reject commission')
        }
    }

    // ========================================================================
    // NEW: Bulk Approve Commissions
    // ========================================================================
    const handleBulkApprove = async () => {
        if (selectedCommissionIds.length === 0) {
            toast.error('Please select commissions to approve')
            return
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/commission/bulk-approve`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
                body: JSON.stringify({
                    commissionIds: selectedCommissionIds,
                    remarks: 'Bulk approved by admin'
                })
            })

            const data = await response.json()

            if (response.ok) {
                toast.success(`Approved ${data.results.approved.length} commissions`)
                setSelectedCommissionIds([])
                fetchPendingCommissions()
                fetchCommissions()
            } else {
                toast.error(data.message || 'Failed to bulk approve')
            }
        } catch (error) {
            console.error('Error bulk approving:', error)
            toast.error('Failed to bulk approve')
        }
    }

    // ========================================================================
    // NEW: Preview Commission Calculation
    // ========================================================================
    const handlePreviewCommission = async () => {
        try {
            setCalculatingCommission(true)
            const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/commission/preview`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
                body: JSON.stringify({
                    startDate: calcStartDate,
                    endDate: calcEndDate,
                    useManualHours: true
                })
            })

            const data = await response.json()

            if (response.ok) {
                setCommissionPreview(data)
            } else {
                toast.error(data.message || 'Failed to preview commission')
            }
        } catch (error) {
            console.error('Error previewing commission:', error)
            toast.error('Failed to preview commission')
        } finally {
            setCalculatingCommission(false)
        }
    }

    // ========================================================================
    // NEW: Calculate and Create Commissions
    // ========================================================================
    const handleCreateCommissions = async () => {
        if (!commissionPreview || commissionPreview.grandTotal === 0) {
            toast.error('No commissions to create')
            return
        }

        if (!(await confirm({ title: 'Create commissions', message: `Create ${commissionPreview.affiliateCount} commission records totaling ₹${commissionPreview.grandTotal.toFixed(2)}?`, confirmText: 'Create' }))) {
            return
        }

        try {
            setCalculatingCommission(true)
            const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/commission/calculate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
                body: JSON.stringify({
                    startDate: calcStartDate,
                    endDate: calcEndDate,
                    periodLabel: commissionPreview.period?.label
                })
            })

            const data = await response.json()

            if (response.ok) {
                toast.success(`Created ${data.created.length} commission records`)
                setCommissionPreview(null)
                fetchPendingCommissions()
                fetchCommissions()
            } else {
                toast.error(data.message || 'Failed to create commissions')
            }
        } catch (error) {
            console.error('Error creating commissions:', error)
            toast.error('Failed to create commissions')
        } finally {
            setCalculatingCommission(false)
        }
    }

    // ========================================================================
    // NEW: Tier Management
    // ========================================================================
    const handleSaveTier = async () => {
        if (!tierForm.name || tierForm.ratePerHour <= 0) {
            toast.error('Please fill all required fields')
            return
        }

        try {
            const url = editingTier
                ? `${process.env.REACT_APP_API_URL}/affiliate/tiers/${editingTier._id}`
                : `${process.env.REACT_APP_API_URL}/affiliate/tiers`

            const response = await fetch(url, {
                method: editingTier ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
                body: JSON.stringify({
                    ...tierForm,
                    maxHours: tierForm.maxHours || null
                })
            })

            const data = await response.json()

            if (response.ok) {
                toast.success(editingTier ? 'Tier updated successfully' : 'Tier created successfully')
                setTierModal(false)
                resetTierForm()
                fetchCommissionTiers()
            } else {
                toast.error(data.message || 'Failed to save tier')
            }
        } catch (error) {
            console.error('Error saving tier:', error)
            toast.error('Failed to save tier')
        }
    }

    const handleDeleteTier = async (tierId) => {
        if (!(await confirm({ title: 'Delete tier', message: 'Are you sure you want to delete this tier?', confirmText: 'Delete', danger: true }))) {
            return
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/tiers/${tierId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            })

            if (response.ok) {
                toast.success('Tier deleted successfully')
                fetchCommissionTiers()
            } else {
                const data = await response.json()
                toast.error(data.message || 'Failed to delete tier')
            }
        } catch (error) {
            console.error('Error deleting tier:', error)
            toast.error('Failed to delete tier')
        }
    }

    const handleSeedDefaultTiers = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/affiliate/tiers/seed`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userInfo.token}`,
                },
            })

            const data = await response.json()

            if (response.ok) {
                toast.success(data.message || 'Default tiers created')
                fetchCommissionTiers()
            } else {
                toast.error(data.message || 'Failed to seed tiers')
            }
        } catch (error) {
            console.error('Error seeding tiers:', error)
            toast.error('Failed to seed tiers')
        }
    }

    const resetTierForm = () => {
        setEditingTier(null)
        setTierForm({
            name: '',
            minHours: 0,
            maxHours: '',
            ratePerHour: 0,
            description: ''
        })
    }

    // Table Columns
    const affiliateColumns = [
        {
            title: 'Name',
            field: 'name',
            render: rowData => (
                <div>
                    <div className="font-medium">{rowData.name}</div>
                    <div className="text-xs text-gray-500">{rowData.phoneNumber}</div>
                </div>
            )
        },
        { title: 'Referral Code', field: 'referralCode' },
        {
            title: 'Total Referrals',
            field: 'totalReferrals',
            render: rowData => <span className="font-semibold text-blue-600">{rowData.totalReferrals || 0}</span>
        },
        {
            title: 'Total Earned',
            field: 'totalEarnings',
            render: rowData => <span className="font-semibold text-green-600">₹{rowData.totalEarnings?.toFixed(2) || '0.00'}</span>
        },
        {
            title: 'Pending',
            field: 'pendingEarnings',
            render: rowData => <span className="font-semibold text-yellow-600">₹{rowData.pendingEarnings?.toFixed(2) || '0.00'}</span>
        },
        {
            title: 'Paid',
            field: 'paidEarnings',
            render: rowData => <span className="font-semibold text-gray-600">₹{rowData.paidEarnings?.toFixed(2) || '0.00'}</span>
        },
        {
            title: 'Actions',
            field: 'actions',
            sorting: false,
            render: rowData => (
                <div className="flex gap-2">
                    <button
                        onClick={() => navigate(`/users/${rowData._id}`)}
                        className="text-blue-600 hover:text-blue-800"
                        title="View Details"
                    >
                        <Visibility fontSize="small" />
                    </button>
                    <button
                        onClick={() => {
                            setSelectedAffiliate(rowData)
                            setAddCommissionModal(true)
                        }}
                        className="text-green-600 hover:text-green-800"
                        title="Add Commission"
                    >
                        <Payment fontSize="small" />
                    </button>
                </div>
            )
        }
    ]

    const commissionColumns = [
        {
            title: 'Date',
            field: 'commissionDate',
            render: rowData => new Date(rowData.commissionDate).toLocaleDateString('en-IN')
        },
        {
            title: 'Affiliate',
            field: 'affiliate',
            render: rowData => (
                <div>
                    <div className="font-medium">{rowData.affiliate?.name}</div>
                    <div className="text-xs text-gray-500">{rowData.affiliate?.phoneNumber}</div>
                </div>
            )
        },
        {
            title: 'Type',
            field: 'type',
            render: rowData => (
                <span className={`px-2 py-1 rounded text-xs font-bold ${rowData.type === 'referral' ? 'bg-blue-100 text-blue-800' :
                    rowData.type === 'bonus' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                    }`}>
                    {rowData.type?.toUpperCase()}
                </span>
            )
        },
        {
            title: 'Amount',
            field: 'amount',
            render: rowData => <span className="font-semibold">₹{rowData.amount?.toFixed(2)}</span>
        },
        {
            title: 'Status',
            field: 'status',
            render: rowData => (
                <span className={`px-2 py-1 rounded text-xs font-bold ${rowData.status === 'paid' ? 'bg-green-100 text-green-800' :
                    rowData.status === 'approved' ? 'bg-blue-100 text-blue-800' :
                        rowData.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                    }`}>
                    {rowData.status?.toUpperCase()}
                </span>
            )
        },
        {
            title: 'Payment Ref',
            field: 'paymentReference',
            render: rowData => rowData.paymentReference || '-'
        },
        { title: 'Remarks', field: 'remarks' },
        {
            title: 'Created By',
            field: 'createdBy',
            render: rowData => rowData.createdBy?.name || '-'
        },
        {
            title: 'Actions',
            field: 'actions',
            sorting: false,
            render: rowData => (
                <div className="flex gap-2">
                    {rowData.status === 'approved' && (
                        <button
                            onClick={() => {
                                setSelectedCommission(rowData)
                                setPaymentModal(true)
                            }}
                            className="text-green-600 hover:text-green-800"
                            title="Process Payment"
                        >
                            <Payment fontSize="small" />
                        </button>
                    )}
                </div>
            )
        }
    ]

    const pendingColumns = [
        {
            title: 'Date',
            field: 'commissionDate',
            render: rowData => new Date(rowData.commissionDate).toLocaleDateString('en-IN')
        },
        {
            title: 'Affiliate',
            field: 'affiliate',
            render: rowData => (
                <div>
                    <div className="font-medium">{rowData.affiliate?.name}</div>
                    <div className="text-xs text-gray-500">{rowData.affiliate?.phoneNumber}</div>
                </div>
            )
        },
        {
            title: 'Type',
            field: 'type',
            render: rowData => (
                <span className={`px-2 py-1 rounded text-xs font-bold ${rowData.type === 'referral' ? 'bg-blue-100 text-blue-800' :
                    rowData.type === 'bonus' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                    }`}>
                    {rowData.type?.toUpperCase()}
                </span>
            )
        },
        {
            title: 'Amount',
            field: 'amount',
            render: rowData => <span className="font-semibold">₹{rowData.amount?.toFixed(2)}</span>
        },
        { title: 'Remarks', field: 'remarks' },
        {
            title: 'Actions',
            field: 'actions',
            sorting: false,
            render: rowData => (
                <div className="flex gap-2">
                    <button
                        onClick={() => handleApproveCommission(rowData._id)}
                        className="text-green-600 hover:text-green-800"
                        title="Approve"
                    >
                        <CheckCircle fontSize="small" />
                    </button>
                    <button
                        onClick={() => handleRejectCommission(rowData._id)}
                        className="text-red-600 hover:text-red-800"
                        title="Reject"
                    >
                        <Cancel fontSize="small" />
                    </button>
                </div>
            )
        }
    ]

    const tierColumns = [
        { title: 'Tier Name', field: 'name', render: rowData => <span className="font-medium">{rowData.name}</span> },
        {
            title: 'Hours Range',
            field: 'minHours',
            render: rowData => (
                <span>
                    {rowData.minHours} - {rowData.maxHours || '∞'} hrs
                </span>
            )
        },
        {
            title: 'Rate/Hour',
            field: 'ratePerHour',
            render: rowData => <span className="font-semibold text-green-600">₹{rowData.ratePerHour}</span>
        },
        { title: 'Description', field: 'description' },
        {
            title: 'Status',
            field: 'isActive',
            render: rowData => (
                <span className={`px-2 py-1 rounded text-xs font-bold ${rowData.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {rowData.isActive ? 'ACTIVE' : 'INACTIVE'}
                </span>
            )
        },
        {
            title: 'Actions',
            field: 'actions',
            sorting: false,
            render: rowData => (
                <div className="flex gap-2">
                    <button
                        onClick={() => {
                            setEditingTier(rowData)
                            setTierForm({
                                name: rowData.name,
                                minHours: rowData.minHours,
                                maxHours: rowData.maxHours || '',
                                ratePerHour: rowData.ratePerHour,
                                description: rowData.description || ''
                            })
                            setTierModal(true)
                        }}
                        className="text-blue-600 hover:text-blue-800"
                        title="Edit"
                    >
                        <Edit fontSize="small" />
                    </button>
                    <button
                        onClick={() => handleDeleteTier(rowData._id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                    >
                        <DeleteOutline fontSize="small" />
                    </button>
                </div>
            )
        }
    ]

    return (
        <div className='w-full h-full'>
            <div className="flex flex-wrap justify-between items-start gap-3 mb-5">
                <ScreenHeader title="Affiliate management" className="!mb-0" />
                <div className="flex gap-2">
                    <button
                        onClick={() => setAssignModal(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow flex items-center gap-2"
                    >
                        <PersonAdd fontSize="small" />
                        Assign Affiliate
                    </button>
                    <button
                        onClick={() => setSessionHoursModal(true)}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded shadow flex items-center gap-2"
                    >
                        <Schedule fontSize="small" />
                        Log Session Hours
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className='bg-white shadow-md rounded-lg overflow-hidden'>
                <div className='flex border-b border-gray-200'>
                    <button
                        className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${activeTab === 'affiliates'
                            ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        onClick={() => setActiveTab('affiliates')}
                    >
                        All Affiliates
                    </button>
                    <button
                        className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${activeTab === 'commissions'
                            ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        onClick={() => setActiveTab('commissions')}
                    >
                        Commission History
                    </button>
                    <button
                        className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${activeTab === 'pending'
                            ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        onClick={() => setActiveTab('pending')}
                    >
                        <span className="flex items-center justify-center gap-1">
                            <Pending fontSize="small" />
                            Pending Approval
                            {pendingCommissions.length > 0 && (
                                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full ml-1">
                                    {pendingCommissions.length}
                                </span>
                            )}
                        </span>
                    </button>
                    <button
                        className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${activeTab === 'calculate'
                            ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        onClick={() => setActiveTab('calculate')}
                    >
                        <span className="flex items-center justify-center gap-1">
                            <Calculate fontSize="small" />
                            Calculate Commission
                        </span>
                    </button>
                    <button
                        className={`flex-1 py-4 px-6 text-center font-medium transition-colors ${activeTab === 'tiers'
                            ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        onClick={() => setActiveTab('tiers')}
                    >
                        <span className="flex items-center justify-center gap-1">
                            <Settings fontSize="small" />
                            Commission Tiers
                        </span>
                    </button>
                </div>

                <div className='p-0'>
                    {activeTab === 'affiliates' && (
                        <MaterialTable
                            title=""
                            icons={tableIcons}
                            columns={affiliateColumns}
                            data={affiliatesData}
                            isLoading={loading}
                            options={{
                                search: true,
                                paging: true,
                                pageSize: 10,
                                headerStyle: { fontWeight: 'bold' },
                                emptyRowsWhenPaging: false,
                                exportButton: true,
                            }}
                        />
                    )}

                    {activeTab === 'commissions' && (
                        <MaterialTable
                            title=""
                            icons={tableIcons}
                            columns={commissionColumns}
                            data={commissionsData}
                            isLoading={loading}
                            options={{
                                search: true,
                                paging: true,
                                pageSize: 10,
                                headerStyle: { fontWeight: 'bold' },
                                emptyRowsWhenPaging: false,
                                exportButton: true,
                            }}
                        />
                    )}

                    {activeTab === 'pending' && (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center bg-yellow-50 p-4 rounded-lg border border-yellow-100">
                                <div className="flex items-center gap-3">
                                    <div className="bg-yellow-100 p-2 rounded-full text-yellow-600">
                                        <Schedule />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-yellow-800">Pending Approvals</h3>
                                        <p className="text-sm text-yellow-700">
                                            {pendingCommissions.length} commissions waiting for approval
                                            {pendingSummary.totalAmount > 0 && ` (Total: ₹${pendingSummary.totalAmount?.toFixed(2)})`}
                                        </p>
                                    </div>
                                </div>
                                {selectedCommissionIds.length > 0 && (
                                    <button
                                        onClick={handleBulkApprove}
                                        className="bg-cm-gold hover:bg-cm-gold-hover text-cm-gold-ink px-4 py-2 rounded shadow flex items-center gap-2"
                                    >
                                        <CheckCircle fontSize="small" />
                                        Approve Selected ({selectedCommissionIds.length})
                                    </button>
                                )}
                            </div>

                            <MaterialTable
                                title=""
                                icons={tableIcons}
                                columns={pendingColumns}
                                data={pendingCommissions}
                                isLoading={loading}
                                options={{
                                    selection: true,
                                    search: true,
                                    paging: true,
                                    pageSize: 10,
                                    headerStyle: { fontWeight: 'bold' },
                                    emptyRowsWhenPaging: false,
                                }}
                                onSelectionChange={(rows) => setSelectedCommissionIds(rows.map(r => r._id))}
                            />
                        </div>
                    )}

                    {activeTab === 'calculate' && (
                        <div className="p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Configuration Panel */}
                                <div className="lg:col-span-1 space-y-6">
                                    <div className="bg-white p-6 rounded-lg border shadow-sm">
                                        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                            <Calculate className="text-emerald-600" />
                                            Calculation Period
                                        </h3>

                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium mb-1 text-gray-700">Start Date</label>
                                                <input
                                                    type="date"
                                                    value={calcStartDate}
                                                    onChange={(e) => setCalcStartDate(e.target.value)}
                                                    className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-emerald-500 outline-none"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium mb-1 text-gray-700">End Date</label>
                                                <input
                                                    type="date"
                                                    value={calcEndDate}
                                                    onChange={(e) => setCalcEndDate(e.target.value)}
                                                    className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-emerald-500 outline-none"
                                                />
                                            </div>

                                            <div className="pt-2">
                                                <button
                                                    onClick={handlePreviewCommission}
                                                    disabled={calculatingCommission}
                                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded font-medium shadow transition-colors disabled:opacity-50 flex justify-center items-center gap-2"
                                                >
                                                    {calculatingCommission ? (
                                                        <>Processing...</>
                                                    ) : (
                                                        <>
                                                            <Visibility fontSize="small" />
                                                            Preview Calculation
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-sm text-blue-800">
                                        <h4 className="font-bold mb-2 flex items-center gap-1">
                                            <CheckCircle fontSize="small" /> Note
                                        </h4>
                                        <p>
                                            This will calculate commissions based on logged session hours and the configured tiered rates.
                                            Existing commissions for this period will be skipped.
                                        </p>
                                    </div>
                                </div>

                                {/* Preview Panel */}
                                <div className="lg:col-span-2">
                                    {commissionPreview ? (
                                        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                                            <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
                                                <h3 className="font-bold text-lg">Calculation Preview</h3>
                                                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded font-bold">
                                                    {commissionPreview.period?.label}
                                                </span>
                                            </div>

                                            <div className="p-6">
                                                <div className="grid grid-cols-3 gap-4 mb-6">
                                                    <div className="bg-purple-50 p-4 rounded border border-purple-100 text-center">
                                                        <div className="text-2xl font-bold text-purple-700">{commissionPreview.affiliateCount}</div>
                                                        <div className="text-xs text-purple-600 font-medium uppercase mt-1">Affiliates</div>
                                                    </div>
                                                    <div className="bg-blue-50 p-4 rounded border border-blue-100 text-center">
                                                        <div className="text-2xl font-bold text-blue-700">{commissionPreview.totalHours?.toFixed(1)}</div>
                                                        <div className="text-xs text-blue-600 font-medium uppercase mt-1">Total Hours</div>
                                                    </div>
                                                    <div className="bg-green-50 p-4 rounded border border-green-100 text-center">
                                                        <div className="text-2xl font-bold text-green-700">₹{commissionPreview.grandTotal?.toFixed(2)}</div>
                                                        <div className="text-xs text-green-600 font-medium uppercase mt-1">Total Commission</div>
                                                    </div>
                                                </div>

                                                <div className="max-h-96 overflow-y-auto border rounded mb-6">
                                                    <table className="w-full text-sm">
                                                        <thead className="bg-gray-50 sticky top-0">
                                                            <tr className="text-left text-gray-500">
                                                                <th className="px-4 py-2 font-medium">Affiliate</th>
                                                                <th className="px-4 py-2 font-medium">Hours</th>
                                                                <th className="px-4 py-2 font-medium">Rate</th>
                                                                <th className="px-4 py-2 font-medium text-right">Commission</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y">
                                                            {commissionPreview.breakdown?.map((item, index) => (
                                                                <tr key={index} className="hover:bg-gray-50">
                                                                    <td className="px-4 py-2 font-medium">{item.affiliateName}</td>
                                                                    <td className="px-4 py-2">{item.totalHours.toFixed(1)}</td>
                                                                    <td className="px-4 py-2 text-gray-500">{item.rateUsed}/hr</td>
                                                                    <td className="px-4 py-2 text-right font-medium text-green-600">₹{item.commissionAmount.toFixed(2)}</td>
                                                                </tr>
                                                            ))}
                                                            {(!commissionPreview.breakdown || commissionPreview.breakdown.length === 0) && (
                                                                <tr>
                                                                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                                                                        No eligible commissions found for this period.
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                <div className="flex justify-end">
                                                    <button
                                                        onClick={handleCreateCommissions}
                                                        className="bg-cm-gold hover:bg-cm-gold-hover text-cm-gold-ink px-6 py-2 rounded font-medium shadow transition-colors flex items-center gap-2"
                                                        disabled={commissionPreview.grandTotal === 0}
                                                    >
                                                        <SaveAlt fontSize="small" />
                                                        Generate & Save Commissions
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="h-full flex flex-col items-center justify-center text-gray-400 p-12 border-2 border-dashed rounded-lg">
                                            <Calculate style={{ fontSize: 64, opacity: 0.2 }} />
                                            <p className="mt-4 font-medium">Select dates and click Preview to calculate commissions</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'tiers' && (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <div className="text-sm text-gray-500">
                                    Configure hourly rate tiers based on total session hours.
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleSeedDefaultTiers}
                                        className="text-emerald-600 hover:text-emerald-800 text-sm font-medium px-3 py-1 border border-emerald-200 rounded hover:bg-emerald-50 transition-colors"
                                    >
                                        Reseed Defaults
                                    </button>
                                    <button
                                        onClick={() => {
                                            resetTierForm()
                                            setTierModal(true)
                                        }}
                                        className="bg-cm-gold hover:bg-cm-gold-hover text-cm-gold-ink px-4 py-2 rounded shadow flex items-center gap-2"
                                    >
                                        <AddBox fontSize="small" />
                                        Add New Tier
                                    </button>
                                </div>
                            </div>

                            <MaterialTable
                                title=""
                                icons={tableIcons}
                                columns={tierColumns}
                                data={commissionTiers}
                                isLoading={loading}
                                options={{
                                    search: false,
                                    paging: false,
                                    headerStyle: { fontWeight: 'bold' },
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Add Commission Modal */}
            {addCommissionModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h2 className="text-xl font-semibold mb-4">Add Commission</h2>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Affiliate</label>
                            <input
                                type="text"
                                value={selectedAffiliate?.name || ''}
                                disabled
                                className="w-full px-3 py-2 border rounded bg-gray-100"
                            />
                            <p className="text-xs text-gray-500 mt-1">{selectedAffiliate?.phoneNumber}</p>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Commission Type</label>
                            <select
                                value={commissionType}
                                onChange={(e) => setCommissionType(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                            >
                                <option value="referral">Referral Commission</option>
                                <option value="bonus">Bonus</option>
                                <option value="adjustment">Adjustment</option>
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Amount (₹)</label>
                            <input
                                type="number"
                                value={commissionAmount}
                                onChange={(e) => setCommissionAmount(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                                placeholder="Enter amount"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Date</label>
                            <input
                                type="date"
                                value={commissionDate}
                                onChange={(e) => setCommissionDate(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Remarks</label>
                            <textarea
                                value={commissionRemarks}
                                onChange={(e) => setCommissionRemarks(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                                rows="3"
                                placeholder="Add any notes..."
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleAddCommission}
                                className="flex-1 bg-cm-gold hover:bg-cm-gold text-cm-gold-ink px-4 py-2 rounded font-medium"
                            >
                                Add Commission
                            </button>
                            <button
                                onClick={() => {
                                    setAddCommissionModal(false)
                                    resetCommissionForm()
                                }}
                                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Process Payment Modal */}
            {paymentModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h2 className="text-xl font-semibold mb-4">Process Payment</h2>

                        <div className="mb-4 bg-gray-50 p-3 rounded">
                            <p className="text-sm"><span className="font-medium">Affiliate:</span> {selectedCommission?.affiliate?.name}</p>
                            <p className="text-sm"><span className="font-medium">Amount:</span> ₹{selectedCommission?.amount?.toFixed(2)}</p>
                            <p className="text-sm"><span className="font-medium">Type:</span> {selectedCommission?.type}</p>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Payment Method</label>
                            <select
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                            >
                                <option value="bank_transfer">Bank Transfer</option>
                                <option value="upi">UPI</option>
                                <option value="cash">Cash</option>
                                <option value="cheque">Cheque</option>
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Payment Reference / Transaction ID</label>
                            <input
                                type="text"
                                value={paymentReference}
                                onChange={(e) => setPaymentReference(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                                placeholder="e.g., TXN123456789"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Payment Remarks</label>
                            <textarea
                                value={paymentRemarks}
                                onChange={(e) => setPaymentRemarks(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                                rows="3"
                                placeholder="Add payment details..."
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleProcessPayment}
                                className="flex-1 bg-cm-gold hover:bg-cm-gold text-cm-gold-ink px-4 py-2 rounded font-medium"
                            >
                                Mark as Paid
                            </button>
                            <button
                                onClick={() => {
                                    setPaymentModal(false)
                                    resetPaymentForm()
                                }}
                                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tier Modal */}
            {tierModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h2 className="text-xl font-semibold mb-4">{editingTier ? 'Edit Tier' : 'Add New Tier'}</h2>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Tier Name</label>
                            <input
                                type="text"
                                value={tierForm.name}
                                onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })}
                                className="w-full px-3 py-2 border rounded"
                                placeholder="e.g., Bronze, Silver"
                            />
                        </div>

                        <div className="flex gap-4 mb-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium mb-2">Min Hours</label>
                                <input
                                    type="number"
                                    value={tierForm.minHours}
                                    onChange={(e) => setTierForm({ ...tierForm, minHours: parseInt(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border rounded"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-sm font-medium mb-2">Max Hours</label>
                                <input
                                    type="number"
                                    value={tierForm.maxHours}
                                    onChange={(e) => setTierForm({ ...tierForm, maxHours: e.target.value ? parseInt(e.target.value) : '' })}
                                    className="w-full px-3 py-2 border rounded"
                                    placeholder="Leave empty for ∞"
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Rate Per Hour (₹)</label>
                            <input
                                type="number"
                                value={tierForm.ratePerHour}
                                onChange={(e) => setTierForm({ ...tierForm, ratePerHour: parseFloat(e.target.value) || 0 })}
                                className="w-full px-3 py-2 border rounded"
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Description</label>
                            <textarea
                                value={tierForm.description}
                                onChange={(e) => setTierForm({ ...tierForm, description: e.target.value })}
                                className="w-full px-3 py-2 border rounded"
                                rows="3"
                                placeholder="Optional description..."
                            />
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleSaveTier}
                                className="flex-1 bg-cm-gold hover:bg-cm-gold text-cm-gold-ink px-4 py-2 rounded font-medium"
                            >
                                Save Tier
                            </button>
                            <button
                                onClick={() => {
                                    setTierModal(false)
                                    resetTierForm()
                                }}
                                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Affiliate Modal */}
            {assignModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-semibold mb-4">Assign Affiliate to Player</h2>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Select Player *</label>
                            <input
                                type="text"
                                value={playerSearch}
                                onChange={(e) => setPlayerSearch(e.target.value)}
                                className="w-full px-3 py-2 border rounded mb-2"
                                placeholder="Search player by name or phone..."
                            />
                            <select
                                value={assignPlayerId}
                                onChange={(e) => setAssignPlayerId(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                            >
                                <option value="">-- Select Player --</option>
                                {allUsers
                                    .filter(u => !playerSearch || u.displayLabel?.toLowerCase().includes(playerSearch.toLowerCase()) || u.name?.toLowerCase().includes(playerSearch.toLowerCase()))
                                    .slice(0, 50)
                                    .map(u => (
                                        <option key={u._id} value={u._id}>{u.displayLabel || u.name}</option>
                                    ))}
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Select Affiliate *</label>
                            <input
                                type="text"
                                value={affiliateSearch}
                                onChange={(e) => setAffiliateSearch(e.target.value)}
                                className="w-full px-3 py-2 border rounded mb-2"
                                placeholder="Search affiliate by name or phone..."
                            />
                            <select
                                value={assignAffiliateId}
                                onChange={(e) => setAssignAffiliateId(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                            >
                                <option value="">-- Select Affiliate --</option>
                                {allUsers
                                    .filter(u => !affiliateSearch || u.displayLabel?.toLowerCase().includes(affiliateSearch.toLowerCase()) || u.name?.toLowerCase().includes(affiliateSearch.toLowerCase()))
                                    .filter(u => u._id !== assignPlayerId)
                                    .slice(0, 50)
                                    .map(u => (
                                        <option key={u._id} value={u._id}>{u.displayLabel || u.name}</option>
                                    ))}
                            </select>
                        </div>

                        <div className="flex gap-4 mb-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium mb-2">Commission Split (%)</label>
                                <input
                                    type="number"
                                    value={assignSplit}
                                    onChange={(e) => setAssignSplit(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                                    className="w-full px-3 py-2 border rounded"
                                    min="0"
                                    max="100"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-sm font-medium mb-2">One-Time Bonus</label>
                                <input
                                    type="number"
                                    value={assignBonus}
                                    onChange={(e) => setAssignBonus(parseFloat(e.target.value) || 0)}
                                    className="w-full px-3 py-2 border rounded"
                                    min="0"
                                    placeholder="0"
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Notes</label>
                            <textarea
                                value={assignNotes}
                                onChange={(e) => setAssignNotes(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                                rows="2"
                                placeholder="Optional notes..."
                            />
                        </div>

                        <div className="bg-blue-50 p-3 rounded mb-4 text-sm text-blue-800">
                            This will manually assign the selected affiliate to the player. The affiliate will earn {assignSplit}% commission split on session hours.
                            {assignBonus > 0 && ` A one-time bonus of ₹${assignBonus} will be created as a pending commission.`}
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleAssignAffiliate}
                                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded font-medium"
                            >
                                Assign Affiliate
                            </button>
                            <button
                                onClick={() => {
                                    setAssignModal(false)
                                    resetAssignForm()
                                }}
                                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Log Session Hours Modal */}
            {sessionHoursModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-semibold mb-4">Log Session Hours</h2>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Select Player *</label>
                            <input
                                type="text"
                                value={sessionPlayerSearch}
                                onChange={(e) => setSessionPlayerSearch(e.target.value)}
                                className="w-full px-3 py-2 border rounded mb-2"
                                placeholder="Search player by name or phone..."
                            />
                            <select
                                value={sessionPlayerId}
                                onChange={(e) => setSessionPlayerId(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                            >
                                <option value="">-- Select Player --</option>
                                {allUsers
                                    .filter(u => !sessionPlayerSearch || u.displayLabel?.toLowerCase().includes(sessionPlayerSearch.toLowerCase()) || u.name?.toLowerCase().includes(sessionPlayerSearch.toLowerCase()))
                                    .slice(0, 50)
                                    .map(u => (
                                        <option key={u._id} value={u._id}>{u.displayLabel || u.name}</option>
                                    ))}
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Select Club *</label>
                            <select
                                value={sessionClubId}
                                onChange={(e) => setSessionClubId(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                            >
                                <option value="">-- Select Club --</option>
                                {clubsList.map(club => (
                                    <option key={club._id} value={club._id}>{club.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex gap-4 mb-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium mb-2">Session Date *</label>
                                <input
                                    type="date"
                                    value={sessionDate}
                                    onChange={(e) => setSessionDate(e.target.value)}
                                    className="w-full px-3 py-2 border rounded"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-sm font-medium mb-2">Hours Played *</label>
                                <input
                                    type="number"
                                    value={sessionHours}
                                    onChange={(e) => setSessionHours(e.target.value)}
                                    className="w-full px-3 py-2 border rounded"
                                    min="0"
                                    step="0.5"
                                    placeholder="e.g., 4.5"
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium mb-2">Remarks</label>
                            <textarea
                                value={sessionRemarks}
                                onChange={(e) => setSessionRemarks(e.target.value)}
                                className="w-full px-3 py-2 border rounded"
                                rows="2"
                                placeholder="Optional remarks..."
                            />
                        </div>

                        <div className="bg-purple-50 p-3 rounded mb-4 text-sm text-purple-800">
                            This will manually log session hours for the selected player. These hours will be used when calculating affiliate commissions.
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={handleLogSessionHours}
                                className="flex-1 bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded font-medium"
                            >
                                Log Hours
                            </button>
                            <button
                                onClick={() => {
                                    setSessionHoursModal(false)
                                    resetSessionForm()
                                }}
                                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AffiliateManagement
